/*
  ========================================================================
  THEME: Impact v7.0.1 merge target + Gro unified rebuild
  STORES: Gro Clinics AU (groclinics.com.au) + NZ (groclinics.co.nz)
  LAST UPDATED: 15/10/2026
  ========================================================================
  gro-article-snapshot.js - opens the hair snapshot pop-up from the
  article hero, loading it only when a reader shows interest.

  OWNERSHIP
    Owns lazy loading of the hosted hair snapshot on article pages. Nothing
    snapshot-related downloads with the article. When a reader points at,
    focuses or taps the button, the snapshot markup is copied out of its
    <template> and the hosted CSS and JS are loaded. The first click then
    opens the pop-up as soon as they are ready. Once loaded, the hosted
    snapshot's own [data-gs-open] listener handles every later click.

  GRO MODULE CONTRACT - read before editing
    Initialisation is idempotent and supports Shopify theme-editor section
    loads. Modern vanilla JavaScript only. No Liquid: the hosted file URLs
    arrive as data attributes on the template element.

  DEPENDENCIES
    snippets/gro-hair-snapshot-popup.liquid (template with data-css,
    data-js and data-font), [data-gro-snapshot-open] buttons, and the hosted
    gro-hair-snapshot.js, which binds [data-gs-open] when it initialises.

  DELIBERATE EXEMPTIONS
    None.
  ========================================================================
*/

(() => {
  if (window.groArticleSnapshotLoaded) return;
  window.groArticleSnapshotLoaded = true;

  let loading = null;

  const loadStyles = (href) => new Promise((resolve) => {
    if (!href || document.querySelector(`link[href="${href}"]`)) {
      resolve();
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.appendChild(link);
  });

  const loadScript = (src) => new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Hair snapshot failed to load'));
    document.body.appendChild(script);
  });

  // Copies the snapshot out of its template and loads the hosted files, once.
  const prepare = () => {
    if (window.__groSnapshotOpenBound) return Promise.resolve();
    if (loading) return loading;

    const template = document.querySelector('template[data-gro-snapshot-template]');
    if (!template) return Promise.reject(new Error('Hair snapshot template missing'));

    if (!document.querySelector('[data-gro-snapshot-host]')) {
      document.body.appendChild(template.content.cloneNode(true));
    }

    loading = Promise.all([
      loadStyles(template.dataset.font),
      loadStyles(template.dataset.css)
    ]).then(() => loadScript(template.dataset.js));

    loading.catch(() => {
      loading = null;
    });
    return loading;
  };

  const track = (button) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'gro_snapshot_cta',
      cta_location: button.dataset.groSnapshotLocation || 'article-hero'
    });
  };

  const bind = (scope) => {
    scope.querySelectorAll('[data-gro-snapshot-open]').forEach((button) => {
      if (button.dataset.groSnapshotBound === 'true') return;
      button.dataset.groSnapshotBound = 'true';

      const prime = () => {
        prepare().catch(() => {});
      };
      button.addEventListener('pointerenter', prime, { once: true });
      button.addEventListener('focus', prime, { once: true });
      button.addEventListener('touchstart', prime, { once: true, passive: true });

      button.addEventListener('click', (event) => {
        if (!event.isTrusted && window.__groSnapshotOpenBound) return; // our own re-click after loading
        track(button);
        if (window.__groSnapshotOpenBound) return; // the hosted snapshot opens itself

        event.preventDefault();
        event.stopPropagation();
        button.setAttribute('aria-busy', 'true');

        prepare()
          .then(() => {
            button.removeAttribute('aria-busy');
            // The hosted snapshot now listens for [data-gs-open] clicks.
            requestAnimationFrame(() => button.click());
          })
          .catch((error) => {
            button.removeAttribute('aria-busy');
            console.warn(error);
          });
      });
    });
  };

  const initialise = (scope = document) => bind(scope);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();